import { MetricEnvelope, readerFor, tableFrom } from './metabaseTable';
import { isWtrRow } from './wtr';

// The popup identifiers the Data team registered as metrics of their own (Phase 1B handoff):
// `POP-CI-xx` answers at tenant grain, `POP-C360-xx` needs the route's `customer_id`. They ride the
// same `GET /v4/dash/customer_intelligence/metrics/{identifier}` route as every metric, but each one
// answers a column schema of its own, so they are read by name through `PopupRow` rather than through
// `MetricResultRow`. PLA-8828.
export const POPUP_IDENTIFIER_PREFIX = 'POP-';

export const isPopupIdentifier = (identifier: string): boolean =>
  typeof identifier === 'string' && identifier.indexOf(POPUP_IDENTIFIER_PREFIX) === 0;

// The one extra parameter POP-C360-14 needs besides `customer_id`, and POP-CI-02's. Without them both
// answer 502.
export const SELECTED_STAGE_ID_KEY = 'selected_stage_id';
export const ORIGINATING_METRIC_ID_KEY = 'originating_metric_id';

const CURRENT_RESULT_ROLE = 'current';
const STATUS_ENTITY_TYPE = 'metric_status';

/** One popup row, read by column name. A column the popup does not declare reads `null`. */
export interface PopupRow {
  text: (name: string) => string | null;
  number: (name: string) => number | null;
  boolean: (name: string) => boolean | null;
}

export interface PopupTable {
  /** The popup's own detail rows: `result_role` current (or undeclared), no status rows, no reasons. */
  rows: PopupRow[];
  /** The `entity_type: metric_status` rows, one per parent metric, which state the response's grain. */
  statusRows: PopupRow[];
  /** Kept for the popups that answer the standard metric schema and parse through `rowsFromEnvelope`. */
  envelope: MetricEnvelope;
}

export const popupTableFromEnvelope = (envelope: MetricEnvelope): PopupTable => {
  const { cols, rows } = tableFrom(envelope);
  const read = readerFor(cols);

  const bind = (row: unknown[]): PopupRow => ({
    text: name => read.text(row, name),
    number: name => read.number(row, name),
    boolean: name => read.boolean(row, name)
  });

  const current = rows.filter(row => {
    const role = read.text(row, 'result_role');

    return !isWtrRow(read, row) && (role === null || role === CURRENT_RESULT_ROLE);
  });
  const isStatus = (row: unknown[]): boolean =>
    read.text(row, 'entity_type') === STATUS_ENTITY_TYPE;

  return {
    rows: current.filter(row => !isStatus(row)).map(bind),
    statusRows: current.filter(isStatus).map(bind),
    envelope
  };
};

export default popupTableFromEnvelope;
